import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { UIPerformance } from '../../core/UIConfig';

interface ColorControlProps {
    value: string;
    onChange: (newValue: string) => void;
    label: string;
    id: string;
}

export const ColorControl = ({ value, onChange, label, id }: ColorControlProps) => {
    const colorInputRef = useRef<HTMLInputElement>(null);

    const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
    };

    const openColorPicker = () => {
        if (colorInputRef.current) {
            colorInputRef.current.click();
        }
    };

    // Convert hex to RGB for cosmic effects
    const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : { r: 255, g: 255, b: 255 };
    };

    const rgb = hexToRgb(value);
    const isLightColor = (rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114) > 128;

    // Check if expensive effects should be disabled
    const config = UIPerformance.getConfig();

    return (
        <div style={styles.container}>
            {/* Label */}
            <motion.label 
                htmlFor={id} 
                style={styles.label}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
            >
                {label}
            </motion.label>
            
            {/* Color Preview Button */}
            <motion.div
                style={styles.colorPreviewContainer}
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                onClick={openColorPicker}
            >
                {/* Cosmic glow based on selected color */}
                <motion.div
                    style={{
                        ...styles.colorGlow,
                        background: `
                            radial-gradient(circle at center, 
                                rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3) 0%, 
                                rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1) 50%, 
                                transparent 100%
                            )
                        `,
                    }}
                    animate={config.disableInfiniteAnimations ? {} : { 
                        opacity: [0.5, 1, 0.5],
                        scale: [1, 1.1, 1] 
                    }}
                    transition={config.disableInfiniteAnimations ? {} : { 
                        duration: 2, 
                        repeat: Infinity, 
                        ease: "easeInOut" 
                    }}
                />
                
                {/* Main color preview */}
                <div style={styles.colorPreview}>
                    {/* Background pattern for transparency */}
                    <div style={styles.transparencyPattern} />
                    
                    {/* Color display */}
                    <div 
                        style={{
                            ...styles.colorDisplay,
                            backgroundColor: value,
                        }}
                    />
                    
                    {/* Color picker icon overlay */}
                    <div style={{
                        ...styles.iconOverlay,
                        color: isLightColor ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
                    }}>
                        <svg 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            style={{ width: '16px', height: '16px' }}
                        >
                            <circle cx="13.5" cy="6.5" r="1.5"/>
                            <circle cx="17.5" cy="10.5" r="1.5"/>
                            <circle cx="8.5" cy="7.5" r="1.5"/>
                            <circle cx="6.5" cy="12.5" r="1.5"/>
                            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 7.012 17.461 2 12 2z"/>
                        </svg>
                    </div>
                    
                    {/* Hex value display */}
                    <div style={styles.hexDisplay}>
                        <span style={{
                            color: isLightColor ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                            fontSize: '10px',
                            fontWeight: '600',
                            fontFamily: 'monospace',
                            textShadow: isLightColor ? '0 0 4px rgba(255, 255, 255, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.8)',
                        }}>
                            {value.toUpperCase()}
                        </span>
                    </div>
                </div>
            </motion.div>
            
            {/* Hidden native color input - positioned better for accessibility */}
            <input
                ref={colorInputRef}
                type="color"
                id={id}
                value={value}
                onChange={handleColorChange}
                style={styles.hiddenInput}
                tabIndex={-1}
            />
        </div>
    );
};

// --- Cosmic Style Definitions ---
const styles: { [key: string]: React.CSSProperties } = {
    container: {
        marginBottom: '16px',
        width: '100%',
        position: 'relative',
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: 'rgba(255, 255, 255, 0.9)',
        marginBottom: '12px',
        letterSpacing: '0.3px',
        textShadow: '0 0 10px rgba(255, 255, 255, 0.2)',
        fontFamily: "'Inter', system-ui, sans-serif",
    },
    colorPreviewContainer: {
        position: 'relative',
        display: 'inline-block',
        cursor: 'pointer',
    },
    colorGlow: {
        position: 'absolute',
        top: '-20px',
        left: '-20px',
        right: '-20px',
        bottom: '-20px',
        borderRadius: '50%',
        pointerEvents: 'none',
        zIndex: 0,
    },
    colorPreview: {
        position: 'relative',
        width: '120px',
        height: '50px',
        borderRadius: '12px',
        overflow: 'hidden',
        background: `
            linear-gradient(135deg, 
                rgba(255, 255, 255, 0.08) 0%, 
                rgba(255, 255, 255, 0.02) 100%
            )
        `,
        backdropFilter: 'blur(15px) saturate(180%)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        boxShadow: `
            0 4px 16px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1),
            0 1px 0 rgba(255, 255, 255, 0.05)
        `,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 1,
    },
    transparencyPattern: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
            linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%), 
            linear-gradient(-45deg, rgba(255,255,255,0.1) 25%, transparent 25%), 
            linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.1) 75%), 
            linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.1) 75%)
        `,
        backgroundSize: '8px 8px',
        backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
        zIndex: 1,
    },
    colorDisplay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2,
        transition: 'all 0.3s ease',
    },
    iconOverlay: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        zIndex: 3,
        transition: 'all 0.3s ease',
        filter: 'drop-shadow(0 0 4px rgba(0, 0, 0, 0.5))',
        pointerEvents: 'none',
    },
    hexDisplay: {
        position: 'absolute',
        bottom: '6px',
        left: '8px',
        zIndex: 3,
        transition: 'all 0.3s ease',
        pointerEvents: 'none',
    },
    hiddenInput: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '1px',
        height: '1px',
        opacity: 0,
        pointerEvents: 'none',
        border: 'none',
        padding: 0,
        margin: 0,
    }
}; 